'use client';

import { useEffect, useState, useTransition } from 'react';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import MissedPrediction from '@/app/[locale]/(main)/profile/_components/ProfilePredictionDialog/MissedPrediction';
import { useClaimWinningsModal } from '@/hooks/useClaimWinningsModal';
import { useMySubmissions } from '@/hooks/useMySubmissions';
import { useUserWinStatus } from '@/hooks/useUserWinStatus';
import { useWinnersData } from '@/hooks/useWinnersData';
import { useWinnersTransform } from '@/hooks/useWinnersTransform';
import { getVariantFromTokenSymbol } from '@/lib/utils/getVariantFromTokenSymbol';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { I_ProfilePredictionDialogContentProps } from '@/ts/interfaces/I_ProfilePredictionDialogContentProps';
import { T_MySubmission } from '@/ts/types/T_MySubmissions';

import { ProfilePredictionDialogCard } from './components/ProfilePredictionDialogCard';
import { ProfilePredictionDialogHeader } from './components/ProfilePredictionDialogHeader';
import { ProfilePredictionDialogTabs } from './components/ProfilePredictionDialogTabs';
import { SubmissionsList } from './components/SubmissionsList';
import { SelectedFlashWinnerTab } from './SelectedFlashWinner';
import { I_Winner } from './WinnerFlashCard';

export default function ProfilePredictionDialogContent({
  closeModal,
  variant = 'BTC',
  type,
  defaultTab = 'prime',
  predictionAddress,
  predictionId,
  isOpen,
  selectedParticipation,
  onClaimingStateChange,
  onClaimSuccess,
  allowConfetti = false
}: I_ProfilePredictionDialogContentProps) {
  const [activeTab, setActiveTab] = useState<'prime' | 'flash'>(
    defaultTab || 'prime'
  );
  const [selectedSubmission, setSelectedSubmission] =
    useState<T_MySubmission | null>(null);

  const [selectedFlashWinner, setSelectedFlashWinner] =
    useState<I_Winner | null>(null);

  const pools = useGlobalData('pools');
  const decimals =
    pools?.find(
      p =>
        p.predictionAddress?.toLowerCase() === predictionAddress?.toLowerCase()
    )?.decimals || 2;

  const validVariant = getVariantFromTokenSymbol(
    selectedParticipation,
    variant
  );

  const {
    primeData,
    flashData,
    isLoadingPrime,
    isLoadingFlash,
    handleLoadMoreFlash,
    realPrice: realPriceFromHook
  } = useWinnersData({
    predictionAddress,
    predictionId,
    type
  });

  // For missed predictions, get realPrice from selectedParticipation
  const realPrice =
    type === 'missed'
      ? selectedParticipation?.predictionEvent?.realPriceDecimal || ''
      : realPriceFromHook;

  const startTime = selectedParticipation?.predictionEvent?.startTime
    ? new Date(
        parseInt(selectedParticipation.predictionEvent.startTime, 10) * 1000
      ).toISOString()
    : undefined;

  const { transformWinners } = useWinnersTransform({
    primeData,
    flashData
  });

  const {
    userHasWonPrime,
    userHasWonFlash,
    shouldShowClaimButton,
    isAlreadyClaimed
  } = useUserWinStatus({
    transformWinners,
    activeTab,
    primeData,
    flashData,
    isLoadingPrime,
    isLoadingFlash,
    selectedParticipation
  });

  const { handleClaimWinnings, isClaiming } = useClaimWinningsModal({
    predictionAddress,
    selectedParticipation,
    onClaimingStateChange,
    closeModal,
    onClaimSuccess
  });

  const {
    submissions,
    total: submissionsTotal,
    winningPrices,
    isLoading: isLoadingSubmissions,
    handleLoadMore: handleLoadMoreSubmissions
  } = useMySubmissions({
    predictionAddress,
    predictionId
  });

  const tokenSymbol = selectedParticipation?.predictionEvent?.tokenSymbol;
  const shortName = selectedParticipation?.predictionEvent?.shortName;

  const shouldShowConfetti =
    (userHasWonPrime || userHasWonFlash) && isOpen && allowConfetti;
  const [showConfetti, setShowConfetti] = useState(false);
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (shouldShowConfetti) {
      setShowConfetti(true);
    } else {
      setShowConfetti(false);
    }
  }, [shouldShowConfetti]);

  return (
    <>
      <div className="w-full h-full bg-transparent flex flex-col">
        <ProfilePredictionDialogHeader
          type={type}
          tokenSymbol={tokenSymbol}
          shortName={shortName}
        />

        <div className="px-6 py-8 flex-col gap-6">
          <ProfilePredictionDialogCard
            variant={validVariant}
            type={type}
            selectedParticipation={selectedParticipation}
            decimals={decimals}
            realPrice={realPrice}
          />

          {type === 'winners' ? (
            selectedFlashWinner ? (
              <SelectedFlashWinnerTab
                selectedFlashWinner={selectedFlashWinner}
                onBack={() => {
                  setSelectedFlashWinner(null);
                  setActiveTab('flash');
                }}
                onClaimWinnings={handleClaimWinnings}
                isClaiming={isClaiming}
                isAlreadyClaimed={isAlreadyClaimed}
                shouldShowClaimButton={shouldShowClaimButton}
                decimals={decimals}
              />
            ) : (
              <ProfilePredictionDialogTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                defaultTab={defaultTab}
                primeData={primeData}
                flashData={flashData}
                isLoadingPrime={isLoadingPrime}
                isLoadingFlash={isLoadingFlash}
                transformWinners={transformWinners}
                userHasWonPrime={userHasWonPrime}
                userHasWonFlash={userHasWonFlash}
                shouldShowClaimButton={shouldShowClaimButton}
                isAlreadyClaimed={isAlreadyClaimed}
                isClaiming={isClaiming}
                onClaimWinnings={handleClaimWinnings}
                onLoadMoreFlash={handleLoadMoreFlash}
                onSelectFlashWinner={setSelectedFlashWinner}
                decimals={decimals}
              />
            )
          ) : selectedSubmission ? (
            <MissedPrediction
              predictedPricesDecimal={selectedSubmission.predictedPricesDecimal}
              winningPrices={winningPrices}
              blockTimestamp={selectedSubmission.blockTimestamp}
              resolutionTime={
                selectedParticipation?.predictionEvent?.resolutionTime
              }
              startTime={startTime}
              onBack={() => setSelectedSubmission(null)}
              isOngoing={type === 'ongoing' || type === 'resolving'}
              decimals={decimals}
            />
          ) : (
            <SubmissionsList
              submissions={submissions}
              total={submissionsTotal}
              isLoading={isLoadingSubmissions}
              type={type}
              winningPrices={winningPrices}
              decimals={decimals}
              onLoadMore={handleLoadMoreSubmissions}
              onSelectSubmission={setSelectedSubmission}
            />
          )}
        </div>
      </div>
      {showConfetti && (
        <div className="fixed left-1/2 top-1/2 h-screen aspect-video -translate-x-1/2 -translate-y-1/2 pointer-events-none z-200 flex items-center justify-center">
          <DotLottieReact
            src="/assets/lotties/confetti.lottie"
            loop={false}
            autoplay
            className="w-full h-full"
            dotLottieRefCallback={ref => {
              ref?.addEventListener('complete', () =>
                startTransition(() => setShowConfetti(false))
              );
            }}
          />
        </div>
      )}
    </>
  );
}
